import { Component, type ErrorInfo, type ReactNode } from 'react'

interface ErrorBoundaryProps {
  children: ReactNode
}

interface ErrorBoundaryState {
  error: Error | null
}

/**
 * Límite de error de la aplicación: captura errores de render de las vistas y
 * muestra una pantalla de recuperación sin derribar todo el shell.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Observabilidad de la UI: registro local (sin telemetría externa en el shell).
    console.error('[AGOS] Error de UI capturado:', error, info.componentStack)
  }

  private handleReset = (): void => {
    this.setState({ error: null })
  }

  render(): ReactNode {
    const { error } = this.state
    if (error) {
      return (
        <div className="error-screen" role="alert">
          <h1 className="error-screen__title">Algo salió mal</h1>
          <p className="error-screen__message">
            Ocurrió un error al mostrar esta vista. Puedes reintentar sin perder la sesión.
          </p>
          <pre className="error-screen__detail">{error.message}</pre>
          <button className="btn btn--primary" onClick={this.handleReset}>
            Reintentar
          </button>
        </div>
      )
    }
    return this.props.children
  }
}
